/* eslint-disable @retail/i18n-checker/require-translate -- backend contract values, not user-facing copy */
import type { DynamicWaveRuleDetail } from '@/api/rule-center/dynamic-rule';

type AiChatRouteKey = {
  routePath?: string;
};

type DynamicWaveRuleSnapshotSource = DynamicWaveRuleDetail & {
  wave_type?: number;
};

const normalizeRouteKey = (value?: string): string =>
  String(value || '').toLowerCase();

const salesOutboundOrderConfigPaths = ['/rulecenter/salesoutboundorderconfig'];
const outboundAllocateRulePaths = ['/rulecenter/allocaterule/sales'];
const dynamicWaveRulePaths = [
  '/rulecenter/waverule/dynamicwaverule',
  '/salesoutbound/task/dynamicwavevirtualtask',
];
const flowPickPaths = [
  '/rulecenter/waverule/flowpicksetting',
  '/salesoutbound/flowpick',
];
const pickingTaskAssignmentPaths = [
  '/rulecenter/pickingrule/taskassignmentrule',
];
const manualWavePaths = ['/salesoutbound/manualwave'];
const waveRulePaths = ['/rulecenter/waverule'];

export const getAiChatBusinessDomain = ({
  routePath,
}: AiChatRouteKey): string | undefined => {
  const normalizedRoutePath = normalizeRouteKey(routePath);

  if (salesOutboundOrderConfigPaths.includes(normalizedRoutePath)) {
    return 'Sales Outbound Order Config';
  }

  if (outboundAllocateRulePaths.includes(normalizedRoutePath)) {
    return 'Outbound Allocate Rule';
  }

  if (
    dynamicWaveRulePaths.some((path) => normalizedRoutePath.startsWith(path))
  ) {
    return 'Dynamic Wave';
  }

  if (flowPickPaths.some((path) => normalizedRoutePath.startsWith(path))) {
    return 'Flow Pick';
  }

  if (pickingTaskAssignmentPaths.includes(normalizedRoutePath)) {
    return 'Picking Task Assignment';
  }

  if (manualWavePaths.includes(normalizedRoutePath)) {
    return 'Manual Wave';
  }

  if (waveRulePaths.some((path) => normalizedRoutePath.startsWith(path))) {
    return 'Wave Rule';
  }

  return undefined;
};

export const isAiChatAvailableByRoute = ({
  routePath,
}: AiChatRouteKey): boolean => !!getAiChatBusinessDomain({ routePath });

export const buildDynamicWaveRuleSnapshot = (
  detail?: DynamicWaveRuleSnapshotSource | null
): string | undefined => {
  if (!detail) return undefined;

  if (
    !detail.rule_id ||
    !detail.rule_name ||
    detail.wave_type === undefined ||
    detail.wave_type === null ||
    Number.isNaN(detail.wave_type)
  ) {
    return undefined;
  }

  return JSON.stringify({
    ...detail,
    rule_id: detail.rule_id ?? '',
    rule_name: detail.rule_name ?? '',
    wave_type: detail.wave_type,
  });
};
